'use client'

import {
  alpha,
  Avatar,
  Box,
  Button,
  Link,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
  Collapse,
  CircularProgress,
  Alert,
  AlertTitle,
  Autocomplete,
} from '@mui/material'
import { ChangeEventHandler, useEffect, useRef, useState } from 'react'
import PersonIcon from '@mui/icons-material/Person'
import { Auth } from 'aws-amplify'
import { CognitoUser } from '@aws-amplify/auth'
import { z } from 'zod'

import config from '@/config'
import usePostLoginRedirect from '../usePostLoginRedirect'
import { useGetMeLazyQuery, useHomeRealmDiscoveryLazyQuery } from '@/generated/graphql'
import { datadogRum } from '@datadog/browser-rum'
import { useRouter, useSearchParams } from 'next/navigation'
import { getDefaultTenant } from '@/helpers/tenants'
import { useAnalytics } from '@/hooks/useAnalytics'
import { useMixPanel } from '@/helpers/mixpanelConfig'
import { SPLITS, useFeatureFlag } from '@/helpers/useFeatureFlag'

export const metadata = {
  title: 'Login | Feedr',
}

const Login = () => {
  const theme = useTheme()
  const mixPanel = useMixPanel()
  const isSmall = useMediaQuery(theme.breakpoints.down('md'))
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  const [fetchSsoSettings, { data, loading, error: ssoSettingsError }] =
    useHomeRealmDiscoveryLazyQuery()
  const ssoSettings = data?.allSsoSettingsByDomain
  const [showPasswordInput, setShowPasswordInput] = useState(false)
  const [showAccountDropdown, setShowAccountDropdown] = useState(false)
  const [disabled, setDisabled] = useState(false)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showError, setShowError] = useState(false)
  const { getDestinationParam, redirect } = usePostLoginRedirect()
  const searchParams = useSearchParams()
  const router = useRouter()
  const [showLegacySSOAlert, setShowLegacySSOAlert] = useState(false)
  const analytics = useAnalytics()
  const canAlwaysShowAccountDropdown = useFeatureFlag(SPLITS.ALWAYS_SHOW_ACCOUNT_DROPDOWN)

  const [getMe] = useGetMeLazyQuery()

  const inputRefPassword = useRef<HTMLInputElement>()
  const inputRefEmail = useRef<HTMLInputElement>()

  useEffect(() => {
    const domValueEmail = inputRefEmail.current?.value
    if (domValueEmail) setEmail(domValueEmail)
  }, [])

  useEffect(() => {
    if (!showLegacySSOAlert && searchParams?.get('fromLegacySSO')) {
      setShowLegacySSOAlert(true)

      const newSearchParams = new URLSearchParams(searchParams)
      newSearchParams.delete('fromLegacySSO')

      router.replace(`/authentication/login?${newSearchParams.toString()}`)
    }
  }, [router, setShowLegacySSOAlert, showLegacySSOAlert, searchParams])

  const preSignInCookieClearance = async () => {
    await Auth.signOut()
    mixPanel?.logout()
  }

  const finishLogin = async (user: CognitoUser) => {
    if (user) {
      const { data } = await getMe()
      const name = `${data?.me.firstName} ${data?.me.lastName}`

      analytics?.identify(data?.me.id, {
        name,
        email: data?.me.email,
        feedr_user: true,
      })

      mixPanel?.login(data?.me)

      const localProxyUrl = searchParams?.get('localProxyUrl')
      if (localProxyUrl && config.FEEDR_ENVIRONMENT !== 'prod') {
        const fullUrl = new URL(localProxyUrl)
        const session = await Auth.currentSession()
        const tenant = getDefaultTenant()
        const prefix = `CognitoIdentityServiceProvider.${tenant?.cognitoConfiguration.userPoolClientId}`
        const encodedAuthUser = encodeURIComponent(email).replace(/%2B/g, '+')
        const cookieName = `${prefix}.${encodedAuthUser}`
        fullUrl.search = [
          `${cookieName}.refreshToken=${session.getRefreshToken().getToken()}`,
          `${cookieName}.accessToken=${session.getAccessToken().getJwtToken()}`,
          `${cookieName}.idToken=${session.getIdToken().getJwtToken()}`,
          `${prefix}.LastAuthUser=${email}`,
          `destination=${getDestinationParam(true)}`,
        ].join('&')
        return window.location.replace(fullUrl.href)
      }
      redirect()
    }
  }

  const onNextClick = async () => {
    try {
      z.string().email().parse(email)
    } catch (err) {
      setShowError(true)
      return
    }

    datadogRum.addAction('LOGIN_EMAIL_SUBMITTED', {
      email: email.toLowerCase(),
      sso: ssoSettings ? ssoSettings[0].enabled || false : false,
    })

    setDisabled(true)
    if (showPasswordInput) {
      try {
        await preSignInCookieClearance()
        Auth.configure({
          authenticationFlowType: 'USER_PASSWORD_AUTH',
        })
        const user: CognitoUser = await Auth.signIn(email, password)
        await finishLogin(user)
      } catch (error) {
        setShowError(true)
        datadogRum.addError(error, {
          email,
          type: 'FAILED_LOGIN',
        })
      }
    } else {
      await fetchSsoSettings({
        variables: {
          domain: email.split('@')[1],
          email: canAlwaysShowAccountDropdown ? null : email.toLowerCase(),
        },
        fetchPolicy: 'no-cache',
      })
    }
    setDisabled(false)
  }

  const onEmailChange: ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement> = (e: any) => {
    setShowError(false)
    setEmail(e.target.value)
    setPassword('')
    setShowPasswordInput(false)
    setShowAccountDropdown(false)
  }

  const onPasswordChange: ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement> = (e: any) => {
    setShowError(false)
    setPassword(e.target.value)
  }

  useEffect(() => {
    if (showPasswordInput && inputRefPassword) inputRefPassword?.current?.focus()
  }, [showPasswordInput])

  const federatedSignIn = async (ssoCode: string) => {
    const destination = getDestinationParam()
    await Auth.federatedSignIn({
      customProvider: ssoCode,
      customState: JSON.stringify({
        ...(destination && { destination }),
      }),
    })
  }

  useEffect(() => {
    if (data || ssoSettingsError) {
      const testSSO = searchParams?.get('testSSO')
      if (!ssoSettings || ssoSettings.length === 1) {
        if (ssoSettings && ssoSettings[0].code?.includes('FEEDR') && !testSSO) {
          setShowPasswordInput(true)
          return
        }
        if (!ssoSettings || !ssoSettings[0].enabled) {
          setShowPasswordInput(true)
        } else {
          setDisabled(true)
          void federatedSignIn(ssoSettings[0].codeOverride || ssoSettings[0].code)
        }
      } else {
        setShowAccountDropdown(true)
      }
    }

    console.error(ssoSettingsError)
  }, [data, ssoSettingsError])

  return (
    <form
      onSubmit={(e: any) => {
        e.preventDefault()
        void onNextClick()
      }}
    >
      <Stack spacing={2} px={isSmall ? 4 : 12}>
        {showLegacySSOAlert && (
          <Alert sx={{ mb: 2 }}>
            <AlertTitle>New Login Experience!</AlertTitle>
            Simply enter your email address below and continue to login like normal. <br />
            Feel free to replace your bookmark with this new page!
          </Alert>
        )}

        <Stack justifyContent="center" alignItems="center">
          {isMobile && (
            <Avatar
              sx={{
                bgcolor: alpha(theme.palette.primary.main, 0.15),
                width: 100,
                height: 100,
              }}
            >
              <PersonIcon color="primary" fontSize="large" />
            </Avatar>
          )}
        </Stack>
        <Typography variant="h3" textAlign={isMobile ? 'center' : 'start'}>
          Hey, welcome back!
        </Typography>
        <TextField
          label="Email"
          variant="outlined"
          value={email}
          id="email"
          onChange={onEmailChange}
          fullWidth
          autoComplete="username"
          data-test-id="login-email-input"
          data-dd-privacy="allow"
        />
        <Collapse in={showPasswordInput}>
          <Stack spacing={1}>
            <TextField
              label="Password"
              variant="outlined"
              type="password"
              id="password"
              autoComplete="password"
              fullWidth
              inputRef={inputRefPassword}
              onChange={onPasswordChange}
              value={password}
              data-test-id="login-password-input"
            />
            <Box>
              <Link href={`/authentication/forgot-password?email=${encodeURIComponent(email)}`}>
                Forgot your password?
              </Link>
            </Box>
            <Collapse in={showError}>
              <Box
                color={theme.palette.error.main}
                bgcolor={alpha(theme.palette.error.main, 0.2)}
                borderRadius={1}
                px={3}
                py={1.5}
                textAlign="center"
              >
                Oops! Your email or password is incorrect. Please try again.
              </Box>
            </Collapse>
          </Stack>
        </Collapse>
        <Collapse in={showAccountDropdown && Boolean(ssoSettings)}>
          <Stack spacing={1}>
            <Autocomplete
              id="account-select-autocomplete"
              options={
                ssoSettings?.map((sso) => ({
                  code: sso.codeOverride || sso.code,
                  label: sso.name || sso.codeOverride || sso.code,
                })) || []
              }
              onChange={(_, account) => {
                if (!account?.code) return
                setDisabled(true)
                federatedSignIn(account.code)
              }}
              renderInput={(params) => (
                <TextField {...params} label="Account" placeholder="Select an account" />
              )}
            />
          </Stack>
        </Collapse>
        <Button
          variant="contained"
          disabled={loading || disabled || (showPasswordInput && !password)}
          type="submit"
          data-test-id="login-next-button"
        >
          {loading || disabled ? <CircularProgress size={25} /> : null}
          {!loading && !disabled ? (showPasswordInput ? 'Log in' : 'Next') : null}
        </Button>
      </Stack>
    </form>
  )
}

export default Login
